import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/kids-playing-river.jpg";
import tubingImg from "@/assets/tubing/family-tubing-fun.jpg";
import campfireImg from "@/assets/gallery/campfire-smores.jpg";

const PUBLISH_DATE = "May 5, 2026";
const CANONICAL = "/blog/fun-things-to-do-with-kids-near-new-braunfels";
const TITLE =
  "Fun Things to Do With Kids Near New Braunfels | Son's";
const DESCRIPTION =
  "Fun things to do with kids near New Braunfels: tubing the Guadalupe, cabanas, glamping, Schlitterbahn, Natural Bridge Caverns & more family favorites.";

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "10 Fun Things to Do With Kids Near New Braunfels",
    description: DESCRIPTION,
    image: "https://sonsguadalupe.com/og-image.png",
    author: { "@type": "Organization", name: "Son's Guadalupe" },
    datePublished: "2026-05-05",
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
    keywords:
      "fun things to do with kids near New Braunfels, family getaways near New Braunfels, tubing the Guadalupe River, cabana rentals near New Braunfels, glamping near New Braunfels, tent camping on River Road",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
      { "@type": "ListItem", position: 3, name: "10 Fun Things to Do With Kids Near New Braunfels", item: `https://sonsguadalupe.com${CANONICAL}` },
    ],
  },
];

const items = [
  { t: "1. Tubing the Guadalupe River", d: "The #1 thing to do near New Braunfels with kids. Calm stretches, gentle rapids, and crystal-clear water — book tubes through Son's Guadalupe and float right from your private cabana." },
  { t: "2. Riverside Cabana Rentals on the Guadalupe", d: "Skip the parking-lot shuffle. Cabana rentals near New Braunfels give your family a shaded home base with a picnic table, river access, and zero stress." },
  { t: "3. Kayaking the Guadalupe", d: "Older kids love paddling our calm cypress-lined stretches. Add a kayak rental to your tubing day for double the fun." },
  { t: "4. Glamping Near New Braunfels", d: "Riverside bell tents with AC and real beds — the perfect upgrade for kids who want adventure without sleeping on the ground." },
  { t: "5. Tent Camping on River Road", d: "If your kids love a true campout, tent camping on River Road is a Texas tradition. Fire pits, stars, and the sound of the river." },
  { t: "6. Schlitterbahn Waterpark", d: "Just minutes from Son's Guadalupe — pair a day at the world-famous waterpark with a relaxing river day." },
  { t: "7. Natural Bridge Caverns", d: "Texas's largest commercial cavern is a short drive from New Braunfels. A great rainy-day backup for family getaways." },
  { t: "8. Gruene Historic District", d: "Live music, ice cream, and Texas history. A family-friendly afternoon stop on any New Braunfels trip." },
  { t: "9. Riverside Picnics & Swimming Holes", d: "Pack lunch and post up at one of our shallow swim spots — perfect for the youngest members of the family." },
  { t: "10. Campfire S'mores at Son's", d: "End every family getaway near New Braunfels around the fire pit. The kids will be talking about it for years." },
];

const FamilyKidsBlog = () => {
  const related = getRelatedPosts("fun-things-to-do-with-kids-near-new-braunfels", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Kids playing in the Guadalupe River near New Braunfels — fun things to do with kids" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                10 Fun Things to Do With Kids Near New Braunfels (Beyond the Tube)
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Planning <strong>family getaways near New Braunfels</strong>? You've picked the right corner of Texas. Between the Guadalupe River, the Hill Country, and a list of attractions a mile long, there's no shortage of <strong>fun things to do with kids near New Braunfels</strong>. Here are our 10 favorites — starting (of course) on the river.
              </p>

              <img src={tubingImg} alt="Family tubing the Guadalupe River near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              {items.map((it, i) => (
                <div key={i}>
                  <h2 className="font-heading text-2xl font-bold text-foreground mt-8">{it.t}</h2>
                  <p>{it.d}</p>
                </div>
              ))}

              <img src={campfireImg} alt="Campfire and s'mores at a family getaway near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-3xl font-bold text-foreground mt-10">Build Your Family Getaway in One Booking</h2>
              <p>
                With Son's Guadalupe, you can book cabana rentals near New Braunfels, tubing the Guadalupe River, glamping near New Braunfels, and tent camping on River Road all in one place. It's the easiest way to plan family getaways near New Braunfels — and the most fun your kids will have all year.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book Now</Button></Link>
                <Link to="/family-getaways-new-braunfels"><Button variant="outline" size="lg" className="text-lg px-10 py-6">Family Getaways</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="More Family Fun"
        heading="Cabanas, Tubing, Glamping & Camping on the Guadalupe"
        paragraphs={[
          "Pair any of these activities with cabana rentals on the Guadalupe River, glamping near New Braunfels, or tent camping on River Road for the complete family getaway near New Braunfels. Son's Guadalupe makes it easy to mix and match.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Tent Camping", to: "/tent-camping-new-braunfels" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default FamilyKidsBlog;
